/**
 * Build Markdown that "Add to document/presentation/…" can hand to an open
 * editor — message text plus any generated/attached images as ![alt](url).
 */

function isImageLikeFile(file) {
  if (!file) return false;
  const mime = String(file.mimeType || file.type || '').toLowerCase();
  if (file.fileType === 'image' || mime.startsWith('image/')) return true;
  const name = String(file.name || file.originalName || file.extension || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp)$/.test(name);
}

function pushImage(chunks, seen, url, alt) {
  const href = String(url || '').trim();
  if (!href || !/^https?:\/\//i.test(href) || seen.has(href)) return;
  seen.add(href);
  const safeAlt = String(alt || 'Image').replace(/[\[\]]/g, '').slice(0, 120) || 'Image';
  chunks.push(`![${safeAlt}](${href})`);
}

/**
 * @returns {string} Markdown ready for editor insert, or '' when nothing usable
 */
export function getMessageEditorInsertMarkdown(message) {
  if (!message) return '';
  const chunks = [];
  const text = String(message.content || '').trim();
  if (text) chunks.push(text);

  const seen = new Set();
  for (const file of message.files || []) {
    if (!isImageLikeFile(file)) continue;
    pushImage(chunks, seen, file.cdnPath || file.url || file.uri, file.name || file.originalName);
  }
  for (const img of message.images || []) {
    pushImage(chunks, seen, img.url || img.cdnPath || img.uri, img.alt || img.name || 'Image');
  }

  return chunks.join('\n\n');
}

export function messageHasEditorInsertContent(message) {
  return getMessageEditorInsertMarkdown(message).length > 0;
}

/** True when the message body contains at least one Markdown pipe-table. */
export function messageHasMarkdownTable(message) {
  const text = String(message?.content || '');
  if (!text.includes('|')) return false;
  // Header row + separator row (| --- | --- |) is the Markdown table signature.
  return /^\s*\|.+\|\s*$/m.test(text) && /^\s*\|?\s*:?-{2,}[-|:\s]*\|?\s*$/m.test(text);
}

/**
 * Pull the first Markdown table in `text` into a 2D string grid. Returns []
 * when no table is found. Used to seed a new spreadsheet from a chat reply.
 *
 * @param {string} text
 * @returns {string[][]}
 */
export function extractFirstMarkdownTableRows(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
  let table = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed.includes('|')) {
      if (table) break;
      continue;
    }
    const cells = trimmed.split('|').map(cell => cell.trim());
    while (cells.length && cells[0] === '') cells.shift();
    while (cells.length && cells[cells.length - 1] === '') cells.pop();
    if (!cells.length) {
      if (table) break;
      continue;
    }
    // Markdown separator row — marks the start of a real table after a header.
    if (cells.every(cell => /^:?-{2,}:?$/.test(cell))) {
      if (!table) table = [];
      continue;
    }
    if (!table) table = [];
    table.push(cells);
  }

  return table || [];
}

/**
 * @returns {string[][]} rows for spreadsheet seeding, or [] when nothing usable
 */
export function getMessageSpreadsheetRows(message) {
  return extractFirstMarkdownTableRows(message?.content || '');
}
